import { Add, Close, EditOutlined } from '@mui/icons-material';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    ThemeProvider,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import PDFViewer from 'components/common/PDFViewer/PDFViewer';
import MilestonesTable, {
    type MilestoneEditableField,
    type MilestoneInvoiceAction,
    type MilestoneStatusField,
    type PendingInvoiceAction,
    type StatusUpdateState,
} from '../components/milestonesTable';

import api, { MilestoneApp } from 'api';
import { MilestonePayload } from 'api/models/payload/MilestonesPayload';
import { FEATURE } from 'constant/features';
import useHasFeature from 'hooks/useHasFeature';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import appThemeV2 from 'theme/themeV2';
import { currencyOptions } from 'utils/currencyOptions';
import { downloadBlob } from 'utils/file';
import { getErrorMessage } from 'utils/v2/errorHelper';

interface AgreementMilestonesModalProps {
    open: boolean;
    onClose: () => void;
    appId: string | null;
    agreementName?: string | null;
    candidateId?: string | null;
    canEditMilestones: boolean;
    /**
     * Defaults to the financials feature. The case agreements editor passes
     * `true` so payment status is editable with the rest of the offer.
     */
    canEditPaymentStatus?: boolean;
    /**
     * Legacy PDF flow re-issues the offer email on save. Template-retainer
     * flows notify the client per retainer instead, so they pass `false`.
     */
    notifyClient?: boolean;
    /** Open straight into the editor (used to create the first offer). */
    startInEditMode?: boolean;
    /**
     * Workflow + payment status on the offer table. Hidden when the table is
     * only used to seed retainer HTML; the case page passes `true` so agents
     * can edit those fields.
     */
    showStatusColumns?: boolean;
    onSaved?: (milestones: MilestoneApp[]) => void;
}

const NEW_MILESTONE_ID_BASE = -1;

const createBlankMilestone = (appId: string, currency: string, index: number): MilestoneApp => ({
    id: NEW_MILESTONE_ID_BASE - index,
    application_id: Number(appId),
    created_at: new Date().toISOString(),
    currency,
    hubspot_id: null,
    description: '',
    name: '',
    price: '',
    status: 'PENDING',
    payment_id: null,
    payment_status: 'PENDING',
});

interface AgreementMilestonesDialogProps extends Omit<AgreementMilestonesModalProps, 'appId'> {
    appId: string;
}

interface InvoicePreview {
    url: string;
    milestoneId: number;
}

const AgreementMilestonesDialog = ({
    open,
    onClose,
    appId,
    agreementName,
    candidateId,
    canEditMilestones,
    canEditPaymentStatus: canEditPaymentStatusProp,
    notifyClient = true,
    startInEditMode = false,
    showStatusColumns = true,
    onSaved,
}: AgreementMilestonesDialogProps) => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const hasFinancials = useHasFeature(FEATURE.FINANCIALS);
    const canEditPaymentStatus = canEditPaymentStatusProp ?? hasFinancials;

    const [milestones, setMilestones] = useState<MilestoneApp[]>([]);
    const [savedMilestones, setSavedMilestones] = useState<MilestoneApp[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [editMode, setEditMode] = useState<boolean>(canEditMilestones && startInEditMode);
    const [currency, setCurrency] = useState<string>('USD');
    const [clientName, setClientName] = useState<string>('');
    const [pendingAction, setPendingAction] = useState<PendingInvoiceAction | null>(null);
    const [statusUpdating, setStatusUpdating] = useState<StatusUpdateState | null>(null);
    const [invoicePreview, setInvoicePreview] = useState<InvoicePreview | null>(null);

    useEffect(() => {
        let cancelled = false;

        const loadMilestones = async () => {
            setLoading(true);
            try {
                const response = await api.application.milestones.get(appId);
                if (!cancelled) {
                    const data = response.data ?? [];
                    setMilestones(data);
                    setSavedMilestones(data);
                    if (data.length > 0 && data[0].currency) {
                        setCurrency(data[0].currency);
                    }
                }
            } catch (error) {
                if (!cancelled) {
                    dispatch(
                        setAlert({
                            message: getErrorMessage(error, translate('somethingWentWrong')),
                            type: 'error',
                        })
                    );
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        void loadMilestones();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, dispatch]);

    // The client name is cosmetic header info, so failures are only logged.
    useEffect(() => {
        if (!candidateId) return undefined;

        let cancelled = false;

        const loadClient = async () => {
            try {
                const response = await api.clientList.getClientById(Number(candidateId));
                if (!cancelled && response.data) {
                    const fullName =
                        `${response.data.firstname ?? ''} ${response.data.lastname ?? ''}`.trim();
                    setClientName(fullName);
                }
            } catch (error) {
                console.error(error);
            }
        };

        void loadClient();

        return () => {
            cancelled = true;
        };
    }, [candidateId]);

    // Object URLs must be released once the preview is dismissed/unmounted
    useEffect(() => {
        return () => {
            if (invoicePreview) {
                window.URL.revokeObjectURL(invoicePreview.url);
            }
        };
    }, [invoicePreview]);

    const fetchInvoiceBlob = async (milestoneId: number): Promise<Blob> => {
        const response = await api.application.getMilestoneInvoice(appId, milestoneId.toString());
        return new Blob([response.data], { type: 'application/pdf' });
    };

    const fetchInvoiceUrl = async (milestoneId: number): Promise<string> => {
        const blob = await fetchInvoiceBlob(milestoneId);
        return window.URL.createObjectURL(blob);
    };

    const handleInvoiceAction = async (action: MilestoneInvoiceAction, milestone: MilestoneApp) => {
        setPendingAction({ milestoneId: milestone.id, action });
        try {
            if (action === 'view') {
                const url = await fetchInvoiceUrl(milestone.id);
                setInvoicePreview({ url, milestoneId: milestone.id });
            } else if (action === 'download') {
                const blob = await fetchInvoiceBlob(milestone.id);
                downloadBlob(blob, `invoice-${milestone.id}.pdf`);
            } else {
                await api.application.sendInvoiceEmail(appId, milestone.id.toString());
                dispatch(
                    setAlert({
                        message: translate('emailSuccessfullySent'),
                        type: 'success',
                    })
                );
            }
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            );
        } finally {
            setPendingAction(null);
        }
    };

    const handleStatusChange = async (
        milestone: MilestoneApp,
        field: MilestoneStatusField,
        value: string
    ) => {
        // Unsaved rows only exist locally; their status is persisted with the bulk save
        if (milestone.id < 0) {
            setMilestones((prev) =>
                prev.map((item) => (item.id === milestone.id ? { ...item, [field]: value } : item))
            );
            return;
        }
        setStatusUpdating({ milestoneId: milestone.id, field });
        try {
            if (field === 'payment_status') {
                await api.application.editMilestonesPaymentStatus(
                    appId,
                    milestone.id.toString(),
                    value
                );
            } else {
                await api.application.editMilestoneStatus(appId, milestone.id.toString(), value);
            }
            setMilestones((prev) =>
                prev.map((item) => (item.id === milestone.id ? { ...item, [field]: value } : item))
            );
            setSavedMilestones((prev) =>
                prev.map((item) => (item.id === milestone.id ? { ...item, [field]: value } : item))
            );
            dispatch(
                setAlert({
                    message: translate('statusUpdated'),
                    type: 'success',
                })
            );
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            );
        } finally {
            setStatusUpdating(null);
        }
    };

    const handleMilestoneFieldChange = (
        milestone: MilestoneApp,
        field: MilestoneEditableField,
        value: string
    ) => {
        if (field === 'price' && value !== '' && Number.isNaN(Number(value))) return;
        setMilestones((prev) =>
            prev.map((item) => (item.id === milestone.id ? { ...item, [field]: value } : item))
        );
    };

    const handleDeleteMilestone = (milestone: MilestoneApp) => {
        setMilestones((prev) => prev.filter((item) => item.id !== milestone.id));
    };

    const handleAddMilestone = () => {
        setMilestones((prev) => [...prev, createBlankMilestone(appId, currency, prev.length)]);
    };

    const handleCancelEdit = () => {
        setMilestones(savedMilestones);
        setCurrency(savedMilestones[0]?.currency || 'USD');
        setEditMode(false);
    };

    const hasEditErrors = milestones.some(
        (milestone) =>
            !milestone.name.trim() ||
            !milestone.description.trim() ||
            !milestone.price.toString().trim()
    );

    /* Same save path as the Retainer Agreement screen: the bulk milestones
     * POST replaces the case's milestone set (which is how local deletions
     * are persisted) and, for the legacy flow, re-issues the offer email. */
    const handleSaveMilestones = async () => {
        if (!milestones.length || hasEditErrors) return;

        setSaving(true);
        try {
            const payload: MilestonePayload[] = milestones.map((item) => ({
                // Rows added locally carry a negative placeholder id
                ...(item.id > 0 ? { id: item.id } : {}),
                name: item.name.trim(),
                description: item.description.trim(),
                price: item.price.toString(),
                currency,
            }));
            await api.application.milestones.post(
                appId,
                payload,
                notifyClient ? { params: { notifyClient: true } } : undefined
            );
            const response = await api.application.milestones.get(appId);
            const data = response.data ?? [];
            setMilestones(data);
            setSavedMilestones(data);
            setEditMode(false);
            onSaved?.(data);
            dispatch(
                setAlert({
                    message: translate(notifyClient ? 'offerSent' : 'offerSaved'),
                    type: 'success',
                })
            );
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            );
        } finally {
            setSaving(false);
        }
    };

    const closeInvoicePreview = () => {
        setInvoicePreview(null);
    };

    const subtitle = [agreementName, clientName].filter(Boolean).join(' — ');

    return (
        <>
            <Dialog
                open={open}
                onClose={saving ? undefined : onClose}
                fullWidth
                maxWidth="lg"
                fullScreen={isMobile}
                scroll="paper"
                aria-labelledby="agreement-milestones-dialog-title"
                sx={{
                    '& .MuiDialog-paper:not(.MuiDialog-paperFullScreen)': {
                        maxWidth: 1080,
                    },
                }}
            >
                <DialogTitle
                    id="agreement-milestones-dialog-title"
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 1,
                        py: 1.5,
                        px: { xs: 2, sm: 2.5 },
                    }}
                >
                    <Stack spacing={0.25} sx={{ minWidth: 0, pr: 0.5 }}>
                        <Typography
                            variant="h6"
                            component="span"
                            sx={{ fontWeight: 700, lineHeight: 1.3 }}
                        >
                            {translate('caseMilestones')}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: 400, fontSize: 13, lineHeight: 1.35 }}
                        >
                            {subtitle || translate('caseMilestonesSubtitle')}
                        </Typography>
                    </Stack>
                    <IconButton
                        onClick={onClose}
                        edge="end"
                        size="small"
                        disabled={saving}
                        aria-label={translate('close')}
                        sx={{ mt: 0.25, flexShrink: 0 }}
                    >
                        <Close fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent
                    dividers
                    sx={{
                        flex: '1 1 auto',
                        minHeight: 0,
                        overflowY: 'auto',
                        px: { xs: 1.5, sm: 2 },
                        py: 1.5,
                        bgcolor: 'rgba(248, 250, 252, 0.55)',
                    }}
                >
                    {canEditMilestones && !editMode && !loading && (
                        <Stack direction="row" sx={{ justifyContent: 'flex-end', mb: 1 }}>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={milestones.length > 0 ? <EditOutlined /> : <Add />}
                                onClick={() => setEditMode(true)}
                                sx={{ textTransform: 'none' }}
                            >
                                {translate(milestones.length > 0 ? 'edit' : 'createOffer')}
                            </Button>
                        </Stack>
                    )}

                    <MilestonesTable
                        milestones={milestones}
                        loading={loading}
                        showInvoiceActions={hasFinancials}
                        canEditPaymentStatus={canEditPaymentStatus}
                        showStatusColumns={showStatusColumns}
                        canEditMilestones={editMode}
                        saving={saving}
                        statusUpdating={statusUpdating}
                        pendingAction={pendingAction}
                        onInvoiceAction={handleInvoiceAction}
                        onStatusChange={handleStatusChange}
                        onMilestoneFieldChange={handleMilestoneFieldChange}
                        onDeleteMilestone={handleDeleteMilestone}
                    />

                    {editMode && (
                        <Stack
                            direction="row"
                            spacing={1.5}
                            useFlexGap
                            sx={{
                                mt: 1.5,
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                            }}
                        >
                            <Button
                                size="small"
                                variant="text"
                                startIcon={<Add />}
                                disabled={saving}
                                onClick={handleAddMilestone}
                                sx={{ textTransform: 'none', mr: 'auto' }}
                            >
                                {translate('addMilestone')}
                            </Button>
                            <TextField
                                select
                                size="small"
                                label={translate('currency')}
                                value={currency}
                                disabled={saving}
                                onChange={(event) => setCurrency(event.target.value)}
                                sx={{ width: 110 }}
                            >
                                {currencyOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Button
                                variant="outlined"
                                disabled={saving}
                                onClick={handleCancelEdit}
                                sx={{ textTransform: 'none' }}
                            >
                                {translate('cancel')}
                            </Button>
                            <Button
                                variant="contained"
                                disabled={saving || hasEditErrors || milestones.length === 0}
                                onClick={handleSaveMilestones}
                                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                            >
                                {saving && (
                                    <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                                )}
                                {translate(notifyClient ? 'saveAndMakeOffer' : 'saveOffer')}
                            </Button>
                        </Stack>
                    )}
                </DialogContent>
            </Dialog>

            {invoicePreview && (
                <Dialog
                    open
                    onClose={closeInvoicePreview}
                    fullWidth
                    maxWidth="md"
                    fullScreen={isMobile}
                    scroll="paper"
                    aria-labelledby="invoice-preview-dialog-title"
                >
                    <DialogTitle
                        id="invoice-preview-dialog-title"
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 1,
                            py: 1.5,
                            px: { xs: 2, sm: 2.5 },
                        }}
                    >
                        <Typography
                            variant="h6"
                            component="span"
                            sx={{ fontWeight: 700, lineHeight: 1.3 }}
                        >
                            {`${translate('invoicePreview')} #${invoicePreview.milestoneId}`}
                        </Typography>
                        <IconButton
                            onClick={closeInvoicePreview}
                            edge="end"
                            size="small"
                            aria-label={translate('close')}
                            sx={{ flexShrink: 0 }}
                        >
                            <Close fontSize="small" />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent
                        dividers
                        sx={{
                            flex: '1 1 auto',
                            minHeight: { xs: 0, sm: '60vh' },
                            overflowY: 'auto',
                            px: { xs: 1.5, sm: 2 },
                            py: 1.5,
                        }}
                    >
                        <PDFViewer file={invoicePreview.url} />
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
};

const AgreementMilestonesModal = ({ open, appId, ...rest }: AgreementMilestonesModalProps) => {
    if (!open || !appId) return null;

    return (
        <ThemeProvider theme={appThemeV2}>
            <AgreementMilestonesDialog open={open} appId={appId} {...rest} />
        </ThemeProvider>
    );
};

export default AgreementMilestonesModal;
